import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronRight } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Grid, Paper, FormHelperText, Stack } from '@mui/material';
import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getJobStatsPostedOnJobBoards,
  IJobBoard,
  ITechFetchJobPostingDTO,
  postToTechFetch,
} from 'wisestep-api';
import {
  WsButton,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsRadioGroup,
  WsButtonGroup,
  IUserDTO,
} from 'wisestep-base';
import { array, object } from 'yup';

import NoDataFoundCard from '../../../../../../components/noDataFoundCard';
import { useUserContext } from '../../../../../../context';
import TechFetch from '../../../../../../images/jobBoardsIcons/TechFetch.png';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import useJob from '../../../../hooks/useJob';
import JobPageParams from '../../../../types/JobPageParams';
import {
  getJobBoards,
  publishCreditsWarning,
  publishFailed,
  publishSuccess,
} from '../../helper/jobBoardHelper';
import SharedUserLists from '../SharedUserLists';

const validationSchema = object().shape({
  workAuthorization: array().required().min(1, 'Work Authorization is required'),
  employmentType: array().required().min(1, 'Employment Type is required'),
  experience: array().required().min(1, 'Experience is required'),
});

const workAuthorizationButtonList = [
  'US Citizen',
  'Security Clearance',
  'EAD (OPT/CPT/GC/H4',
  'H1B Visa',
  'Need new H1B Visa',
  'Other',
];
const employmentTypeButtonList = [
  'Corp-corp',
  'W2-Permanent',
  'W2-Contract',
  '1099-Contract',
  'Contract to Hire',
  'Need H1B',
];
const experienceButtonList = [
  'Junior ( < 3 Yrs)',
  'Intermediate (3-5 Yrs)',
  'Senior (5-7 Yrs)',
  'Architect ( < 7 Yrs)',
];

const techFetchValues: ITechFetchJobPostingDTO = {
  workAuthorization: [],
  employmentType: [],
  experience: [],
  jobIds: [],
  clientEmail: '',
  state: '',
  city: '',
  canShowSalary: false,
};
interface ITFCount {
  TechFetchCount: number;
}

const TechFetchAccount = () => {
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValue: techFetchValues,
  };
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const navigate = useNavigate();
  const [techFetchResponse, setTechFetchResponse] = useState<IJobBoard[]>([]);
  const [techFetchSalary, setTechFetchSalary] = useState<string>('');
  const params = useParams<JobPageParams>();
  const jobId = params.jobId ?? '';
  const [techFetchEmailsData, setTechFetchEmailsData] = useState<IJobBoard[]>([]);
  const methods = useForm<ITechFetchJobPostingDTO>(formOptions);
  const {
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const setNotification = useNotificationStore((state) => state.setNotification);

  const [jobDetails] = useJob(jobId);
  const [jobStatsPostedOnJobBoard, setJobStatsPostedOnJobBoards] = useState<ITFCount>();

  const onWorkAuthorizationChange = (index: number[], value: string[]) =>
    setValue('workAuthorization', value);

  const onEmploymentTypeChange = (index: number[], value: string[]) =>
    setValue('employmentType', value);

  const onExperienceChange = (index: number[], value: string[]) => setValue('experience', value);

  const getJobStatsPostedOnJobBoard = async () => {
    const response = await getJobStatsPostedOnJobBoards();
    setJobStatsPostedOnJobBoards(response.data);
  };

  useEffect(() => {
    const setTechFetchJobBoard = async () => {
      const allAccounts = await getJobBoards('TechFetch');
      const techFetchAccountDetails = allAccounts.filter(
        (account: IJobBoard) =>
          account.jobBoardName === 'TechFetch' &&
          account.jobBoardSharedUsers &&
          account.jobBoardSharedUsers?.filter((shared) => shared.email == loggedInUser.email)
            .length > 0
      );
      setTechFetchResponse(techFetchAccountDetails);
      reset({
        clientEmail: techFetchAccountDetails?.[0].username,
      });
    };
    setTechFetchJobBoard();
    getJobStatsPostedOnJobBoard();
  }, [loggedInUser.email, reset]);

  const techFetchClientEmail = techFetchResponse
    ?.filter(
      (account) =>
        account.jobBoardSharedUsers &&
        account.jobBoardSharedUsers?.filter(
          (sharedAccount) => sharedAccount.email == loggedInUser.email
        ).length > 0
    )
    .map((account) => ({
      label: account.username,
      value: account.username,
    }));

  const handleTechFetchSalary = (index: number) => {
    const salaryDetails = `${jobDetails?.salaryCurrency} ${jobDetails?.minimumSalary} -
     ${jobDetails?.maximumSalary} ${jobDetails?.salaryTimeSpan}`;

    if (index == 0) {
      setTechFetchSalary(salaryDetails ?? 'Salary not found');
    } else {
      setTechFetchSalary('');
    }
  };

  const onFormSubmit = async (data: ITechFetchJobPostingDTO) => {
    const selectAccount: IJobBoard[] = techFetchEmailsData;
    if (selectAccount.length > 0 && loggedInUser) {
      const maxPostingAvailableCount = selectAccount[0].jobBoardSharedUsers?.filter(
        (e) => e.email == loggedInUser.email
      )[0]?.maxPostingsPerMonth;

      if (
        !maxPostingAvailableCount ||
        (jobStatsPostedOnJobBoard &&
          jobStatsPostedOnJobBoard?.TechFetchCount >= maxPostingAvailableCount)
      ) {
        setNotification(publishCreditsWarning());
        return;
      }
    }
    try {
      if (jobId) {
        data.jobIds = [jobId];
        await postToTechFetch(data);
        setNotification(publishSuccess());
        navigate(`/jobs/${jobId}/publish`);
      }
    } catch {
      setNotification(publishFailed());
    }
  };
  const watchClientEmail = watch('clientEmail') ?? '';

  useEffect(() => {
    const selectAccount: IJobBoard[] =
      techFetchResponse?.filter((account) => account.username === watchClientEmail) ?? [];
    setTechFetchEmailsData(selectAccount);
  }, [techFetchResponse, watchClientEmail]);

  return (
    <Paper sx={{ pt: 2 }}>
      <Grid item xs={6} container columnGap={2}>
        <ChevronLeft onClick={() => navigate(`/jobs/${jobId}/publish`)} />
        <img src={TechFetch} alt="TechFetch" height="35" />
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
          TechFetch
        </WsTypography>
      </Grid>
      {techFetchClientEmail?.length > 0 ? (
        <Grid>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Paper sx={{ p: 2 }}>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Choose an account *
                    </WsTypography>
                    <WsButton
                      variant="text"
                      size="large"
                      onClick={() => navigate('/job-boards/techFetch')}
                    >
                      Configure accounts
                    </WsButton>
                  </Grid>
                  {techFetchClientEmail && (
                    <Grid item xs={4} pl={1}>
                      <WsRadioGroup
                        name="clientEmail"
                        label=""
                        selectArray={techFetchClientEmail ?? []}
                        row={false}
                        size="medium"
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Work Authorization
                    </WsTypography>
                  </Grid>
                  <Stack direction="row" spacing={1}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={workAuthorizationButtonList}
                      selectedMultipleIndexValue={onWorkAuthorizationChange}
                      multiple={true}
                    />
                    <FormHelperText error>
                      {errors.workAuthorization ? errors.workAuthorization['message'] : ''}
                    </FormHelperText>
                  </Stack>
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Employment Type
                    </WsTypography>
                  </Grid>
                  <Stack direction="row" spacing={1}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={employmentTypeButtonList}
                      selectedMultipleIndexValue={onEmploymentTypeChange}
                      multiple={true}
                    />
                    <FormHelperText error>
                      {errors.employmentType ? errors.employmentType['message'] : ''}
                    </FormHelperText>
                  </Stack>
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Experience
                    </WsTypography>
                  </Grid>
                  <Stack direction="row" spacing={1}>
                    <WsButtonGroup
                      inputTitle=" "
                      buttonsList={experienceButtonList}
                      selectedMultipleIndexValue={onExperienceChange}
                      multiple={true}
                    />
                    <FormHelperText error>
                      {errors.experience ? errors.experience['message'] : ''}
                    </FormHelperText>
                  </Stack>
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Salary
                    </WsTypography>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      {techFetchSalary}
                    </WsTypography>
                  </Grid>
                  <Grid item xs={4} pl={1}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={['Show', 'Hide']}
                      selectedIndexValue={(index: number) => handleTechFetchSalary(index)}
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Grid container justifyContent="flex-end">
                  <WsButton
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<ChevronRight />}
                  >
                    Publish
                  </WsButton>
                </Grid>
              </Paper>
            </form>
          </FormProvider>
          <Grid container item xs={12}>
            {techFetchEmailsData && techFetchEmailsData.length > 0 && (
              <SharedUserLists
                value={techFetchEmailsData[0].username}
                selectedAccount={techFetchEmailsData}
                usedCount={jobStatsPostedOnJobBoard?.TechFetchCount ?? 0}
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <NoDataFoundCard text="No Job posting credits allocated to your account, Contact Admin" />
      )}
    </Paper>
  );
};

export default TechFetchAccount;
